import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../hooks/useAuth';
import '../styles/IAPage.css';
import { authFetch } from '../utils/authFetch';

// --- Estrutura de Tipos para as Mensagens ---

// Dados para uma única linha da tabela
interface TableData {
  id: number;
  processo: string;
  po_sap: string;
  item: string;
  quantidade: number;
  eta_mao: string;
}

// Conteúdo quando a resposta é uma tabela
interface TableContent {
  type: 'multiple_results';
  preamble: string;
  data: TableData[];
  postamble: string;
}

// Conteúdo quando a resposta é texto
interface TextContent {
  type: 'single_result';
  content: string;
}

// O tipo de conteúdo de uma mensagem do Jotinha pode ser um dos dois
type JotinhaContent = TableContent | TextContent;

// A mensagem pode ter um conteúdo de string (do usuário) ou um objeto (do Jotinha)
interface Message {
  role: 'user' | 'jotinha';
  content: string | JotinhaContent;
}

// --- Componente para Renderizar a Tabela Clicável ---
const ResultsTable: React.FC<{ data: TableData[], onRowClick: (id: number) => void }> = ({ data, onRowClick }) => (
  <div className="table-container">
    <table className="results-table">
      <thead>
        <tr>
          <th>Processo</th>
          <th>PO SAP</th>
          <th>Item</th>
          <th>Quantidade</th>
          <th>ETA MAO</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row) => (
          <tr key={row.id} onClick={() => onRowClick(row.id)} className="clickable-row" title="Clique para ver detalhes">
            <td>{row.processo}</td>
            <td>{row.po_sap}</td>
            <td>{row.item}</td>
            <td>{row.quantidade}</td>
            <td>{row.eta_mao}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// --- Componente Principal da Página ---
export const IAPage = () => {
  const auth = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatWindowRef = useRef<HTMLDivElement>(null);

  // Efeito para a mensagem de boas-vindas
  useEffect(() => {
    const userFirstName = auth.email?.split('@')[0].split('.')[0];
    const capitalizedFirstName = userFirstName ? userFirstName.charAt(0).toUpperCase() + userFirstName.slice(1) : 'usuário';
    
    const welcomeMessage: Message = {
      role: 'jotinha',
      content: {
        type: 'single_result',
        content: `Olá, **${capitalizedFirstName}**! Eu sou o Jotinha, seu assistente de Control Tower (CT) aqui na CCOMEX. Para consultar os detalhes de um embarque, por favor, digite o código do **PROCESSO, PO SAP, INVOICE, BL/AWB ou #CNTR / MAWB** no campo abaixo e pressione Enviar.`
      }
    };
    setMessages([welcomeMessage]);
  }, [auth.email]);

  // Efeito para rolar o chat para o final a cada nova mensagem
  useEffect(() => {
    if (chatWindowRef.current) {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Função para enviar a mensagem do usuário
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await authFetch('/api/ia/chat', {
        method: 'POST',
        body: JSON.stringify({ messages: [userMessage] })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ocorreu um erro na busca.');
      }

      const data = await response.json();
      const jotinhaMessage: Message = { role: 'jotinha', content: data.answer };
      setMessages(prev => [...prev, jotinhaMessage]);

    } catch (error: any) {
      const errorMessage: Message = { 
        role: 'jotinha', 
        content: { type: 'single_result', content: error.message || 'Desculpe, não consegui processar sua busca.' }
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Função para lidar com o clique na linha da tabela
  const handleRowClick = async (rowId: number) => {
    if (isLoading) return;
    setIsLoading(true);

    const feedbackMessage: Message = {
      role: 'jotinha',
      content: { type: 'single_result', content: `Buscando detalhes do item selecionado...` }
    };
    setMessages(prev => [...prev, feedbackMessage]);

    try {
      const response = await authFetch(`/api/forakit/details/${rowId}`);
      if (!response.ok) {
        throw new Error('Não foi possível buscar os detalhes do item selecionado.');
      }
      const data = await response.json();
      
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages.pop(); // Remove a mensagem de feedback "Buscando..."
        newMessages.push({ role: 'jotinha', content: data });
        return newMessages;
      });

    } catch (error: any) {
      const errorMessage: Message = { 
        role: 'jotinha', 
        content: { type: 'single_result', content: error.message }
      };
       setMessages(prev => {
        const newMessages = [...prev];
        newMessages.pop(); // Remove a mensagem de feedback "Buscando..."
        newMessages.push(errorMessage);
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="ia-page-container">
      <div className="chat-window" ref={chatWindowRef}>
        {messages.map((msg, index) => (
          <div key={index} className={`chat-message ${msg.role}`}>
            <div className="chat-message-text">
              {/* Lógica para renderizar texto, tabela ou mensagem do usuário */}
              {typeof msg.content === 'string' ? (
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              ) : msg.content.type === 'multiple_results' ? (
                <>
                  <ReactMarkdown>{msg.content.preamble}</ReactMarkdown>
                  <ResultsTable data={msg.content.data} onRowClick={handleRowClick} />
                  <ReactMarkdown>{msg.content.postamble}</ReactMarkdown>
                </>
              ) : (
                <ReactMarkdown>{msg.content.content}</ReactMarkdown>
              )}
            </div>
          </div>
        ))}
        {/* Mostra o feedback de "Buscando..." fora do balão de mensagem para melhor UX */}
        {isLoading && <div className="loading-indicator">Buscando...</div>}
      </div>
      <div className="chat-input-form">
        <textarea
          className="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Digite o código do PROCESSO, PO SAP, INVOICE, etc..."
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={isLoading}
          rows={1}
        />
        <button className="send-button" onClick={handleSend} disabled={isLoading}>
          Enviar
        </button>
      </div>
    </div>
  );
};
